/**
 * VideoRecorderWithMixedAudio
 * Records video with BOTH question audio and microphone audio mixed together
 * Uses Web Audio API to mix audio sources before recording
 */
import { getPublicApiOrigin } from '../config/env';
import RecordingCompositor from './RecordingCompositor';
import RecordingUploader from './RecordingUploader';

class VideoRecorderWithMixedAudio {
  constructor(sessionId) {
    console.log('🎬 VideoRecorderWithMixedAudio: Constructor called', { sessionId });
    
    this.sessionId = sessionId;
    this.recordedChunks = [];
    this.mediaRecorder = null;
    this.audioContext = null;
    this.audioDestination = null;
    this.micSource = null;
    this.questionAudioSource = null;
    this.questionAudioElement = null;
    this.videoElement = null;
    this.micStream = null;
    this.questionAnalyser = null;
    this.micAnalyser = null;
    this.mimeType = null;
    this.isRecording = false;
    this.isInitialized = false;
    this.recordingStartTime = null;
    /** Set while awaiting onstop → finalize (see stopAsync) */
    this._resolveUpload = null;
    /** Combined camera video + mixed mic/TTS audio (for LiveKit reuse) */
    this.combinedStream = null;
    /** Avoid duplicate partial uploads */
    this._partialUploadInFlight = false;
    this._uploadCompleted = false;
    this._uploadInFlight = false;
    this._beforeUnloadHandler = null;
    /** Camera-first, then screen + face bubble (RecordingCompositor); null → raw camera. */
    this.compositor = null;
    /** Uploads slices while recording (RecordingUploader); null → one upload at the end. */
    this.uploader = null;
  }

  _candidateToken() {
    return localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  }

  /**
   * Initialize audio context and routing BEFORE any audio plays
   * This MUST be called once at the start, before questions play
   * @param {HTMLVideoElement} videoElement - Video element with camera stream
   * @param {HTMLAudioElement} questionAudioElement - Audio element that will play questions
   * @returns {Promise<void>}
   */
  async initialize(videoElement, questionAudioElement, { screenStream = null } = {}) {
    try {
      console.log('🎬 Initializing video recorder with mixed audio...');
      
      if (!videoElement || !videoElement.srcObject) {
        throw new Error('Video element or stream not available');
      }
      
      if (!questionAudioElement) {
        console.warn('⚠️ Question audio element not provided - questions will not be recorded');
      }
      
      // Store references
      this.videoElement = videoElement;
      this.questionAudioElement = questionAudioElement;
      
      // Create audio context (must be done in user gesture context)
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      console.log('✅ Audio context created, state:', this.audioContext.state);
      
      // Resume audio context if suspended (required by some browsers)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
        console.log('✅ Audio context resumed');
      }
      
      // Create destination for mixed audio
      this.audioDestination = this.audioContext.createMediaStreamDestination();
      console.log('✅ Audio destination created');
      
      // Get video stream
      const videoStream = videoElement.srcObject;
      if (!videoStream || !videoStream.active) {
        throw new Error('Video stream is not active');
      }
      
      // Get microphone stream (separate from video stream for better control)
      console.log('🎤 Requesting microphone access...');
      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000
        }
      });
      console.log('✅ Microphone access granted');
      
      // Connect microphone to mixer
      this.micSource = this.audioContext.createMediaStreamSource(this.micStream);
      this.micSource.connect(this.audioDestination);
      console.log('✅ Microphone connected to mixer');
      
      // Connect question audio element to mixer
      // CRITICAL: This must be done BEFORE any audio plays
      if (questionAudioElement) {
        try {
          // Set crossOrigin to allow audio capture
          questionAudioElement.crossOrigin = 'anonymous';
          
          // Create MediaElementSource (can only be called once per element!)
          try {
            this.questionAudioSource =
              this.audioContext.createMediaElementSource(questionAudioElement);
          } catch (e) {
            if (e.name === 'InvalidStateError') {
              console.warn(
                '⚠️ Audio element already has a MediaElementSource — reusing existing node'
              );
              // Source already created — skip, audio will still be captured via mic
            } else {
              throw e;
            }
          }
          
          // Create gain nodes for volume control
          const micGain = this.audioContext.createGain();
          const questionGain = this.audioContext.createGain();
          
          // Set volumes (adjust as needed)
          micGain.gain.value = 1.0;        // 100% microphone volume
          questionGain.gain.value = 0.8;  // 80% question volume (slightly lower)
          
          // Connect microphone with gain
          this.micSource.disconnect(); // Disconnect direct connection
          this.micSource.connect(micGain);
          micGain.connect(this.audioDestination);
          
          // Connect question audio with gain
          // Connect to mixer (for recording)
          this.questionAudioSource.connect(questionGain);
          questionGain.connect(this.audioDestination);
          
          // ALSO connect to speakers (so user can hear it)
          questionGain.connect(this.audioContext.destination);

          // Connect realtime AnalyserNodes for UI waveform visualization
          try {
            this.questionAnalyser = this.audioContext.createAnalyser();
            this.questionAnalyser.fftSize = 256;
            this.questionAnalyser.smoothingTimeConstant = 0.8;
            questionGain.connect(this.questionAnalyser);

            this.micAnalyser = this.audioContext.createAnalyser();
            this.micAnalyser.fftSize = 256;
            this.micAnalyser.smoothingTimeConstant = 0.8;
            micGain.connect(this.micAnalyser);
          } catch (analyserErr) {
            console.warn('⚠️ Could not connect analyser nodes:', analyserErr);
          }
          
          console.log('✅ Question audio connected to mixer AND speakers');
          console.log('   - Microphone: 100% volume');
          console.log('   - Question: 80% volume');
        } catch (e) {
          console.error('❌ Failed to connect question audio:', e);
          console.warn('⚠️ Questions will play but NOT be recorded!');
          // Continue without question audio recording
        }
      }
      
      // Video: the compositor's canvas track (camera for the first minute, then the
      // shared screen with a camera bubble). Falls back to the raw camera track if the
      // browser can't composite (no canvas.captureStream).
      let videoTrack = null;
      try {
        this.compositor = new RecordingCompositor(videoElement);
        if (screenStream) this.compositor.setScreenStream(screenStream);
        videoTrack = this.compositor.videoTrack;
      } catch (e) {
        console.warn('⚠️ Recording compositor unavailable, recording the camera only:', e);
        this.compositor = null;
      }
      if (!videoTrack) videoTrack = videoStream.getVideoTracks()[0];
      const mixedAudioTrack = this.audioDestination.stream.getAudioTracks()[0];
      
      if (!videoTrack) {
        throw new Error('No video track in stream');
      }
      
      if (!mixedAudioTrack) {
        throw new Error('No audio track in mixed stream');
      }
      
      const combinedStream = new MediaStream([videoTrack, mixedAudioTrack]);
      this.combinedStream = combinedStream;

      console.log('✅ Combined stream created:', {
        videoTracks: combinedStream.getVideoTracks().length,
        audioTracks: combinedStream.getAudioTracks().length
      });
      
      // Get supported MIME type
      this.mimeType = this.getSupportedMimeType();
      console.log('✅ Using MIME type:', this.mimeType);
      
      // Create MediaRecorder
      try {
        this.mediaRecorder = new MediaRecorder(combinedStream, {
          mimeType: this.mimeType,
          // Screen content (code, text) needs more bits than a talking head; kept modest so
          // the continuous upload doesn't crowd out the live interview on weak uplinks.
          videoBitsPerSecond: this.compositor ? VideoRecorderWithMixedAudio.COMPOSITE_BPS : 500000,
          audioBitsPerSecond: 128000
        });
        console.log('✅ MediaRecorder created');
      } catch (error) {
        console.warn('⚠️ Failed to create MediaRecorder with options, trying default:', error);
        this.mediaRecorder = new MediaRecorder(combinedStream);
        this.mimeType = this.mediaRecorder.mimeType || 'video/webm';
        console.log('✅ MediaRecorder created with default options');
      }
      
      // Setup event handlers
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          if (this.uploader && !this.uploader.failed) {
            // Continuous upload: the slice goes to the server now, not at the end.
            this.uploader.push(event.data);
            return;
          }
          this.recordedChunks.push(event.data);
          const totalSize = this.recordedChunks.reduce((sum, chunk) => sum + chunk.size, 0);
          console.log(`📹 Chunk collected: ${event.data.size} bytes (total: ${(totalSize / 1024 / 1024).toFixed(2)} MB)`);
        }
      };
      
      this.mediaRecorder.onstop = async () => {
        console.log(`🛑 Recording stopped. Total chunks: ${this.recordedChunks.length}`);
        this.isRecording = false;
        try {
          await this.finalizeAndUpload();
        } finally {
          if (this._resolveUpload) {
            this._resolveUpload();
            this._resolveUpload = null;
          }
        }
      };
      
      this.mediaRecorder.onerror = (event) => {
        console.error('❌ MediaRecorder error:', event.error);
        this.isRecording = false;
        void this.savePartialRecording();
      };

      this._beforeUnloadHandler = () => {
        if (this.uploader && !this.uploader.failed) {
          // Flush the current slice; the uploader sends it (or keeps it in IndexedDB).
          try {
            if (this.mediaRecorder?.state === 'recording') this.mediaRecorder.requestData();
          } catch (e) {
            /* ignore */
          }
          return;
        }
        void this.savePartialRecording();
      };
      window.addEventListener('beforeunload', this._beforeUnloadHandler);
      
      this.isInitialized = true;
      console.log('✅ Video recorder fully initialized and ready');
      
    } catch (error) {
      console.error('❌ Initialization failed:', error);
      this.isInitialized = false;
      throw error;
    }
  }

  /**
   * Start recording
   */
  start() {
    console.log(
      '🎬 VideoRecorder.start() called, mimeType:',
      this.mimeType,
      'stream tracks:',
      this.combinedStream?.getTracks()?.length
    );
    if (!this.isInitialized) {
      throw new Error('Recorder not initialized. Call initialize() first.');
    }
    
    if (!this.mediaRecorder) {
      throw new Error('MediaRecorder not created');
    }
    
    if (this.mediaRecorder.state === 'inactive') {
      this.recordingStartTime = Date.now();
      this.compositor?.markRecordingStart();
      // Each MediaRecorder start is a new WebM stream → its own part on the server.
      const apiOrigin = getPublicApiOrigin();
      if (apiOrigin) {
        this.uploader = new RecordingUploader(this.sessionId, {
          apiOrigin,
          getToken: () => this._candidateToken(),
          onFallback: (blobs) => { this.recordedChunks.push(...blobs); },
        });
        void this.uploader.begin();
      }
      // Short slices = a steady trickle of small uploads instead of ~1 MB bursts that queue the
      // candidate's speech / interview messages behind them on a weak uplink.
      this.mediaRecorder.start(VideoRecorderWithMixedAudio.TIMESLICE_MS);
      console.log(
        `▶️ MediaRecorder.start() called with timeslice ${VideoRecorderWithMixedAudio.TIMESLICE_MS}ms, state:`,
        this.mediaRecorder?.state
      );
      this.isRecording = true;
      console.log('🎥 Recording STARTED - capturing video + question audio + microphone');
    } else {
      console.warn('⚠️ Recorder already running, state:', this.mediaRecorder.state);
    }
  }

  /**
   * Stop recording (fire-and-forget). Prefer {@link #stopAsync} before closing sockets.
   */
  stop() {
    void this.stopAsync();
  }

  /**
   * Stop MediaRecorder and wait until upload + cleanup complete (or no-op if nothing recorded).
   * @returns {Promise<void>}
   */
  async stopAsync() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      console.log('🛑 Stopping recording...');
      const done = new Promise((resolve) => {
        this._resolveUpload = resolve;
      });
      try {
        if (this.mediaRecorder.state === 'recording') {
          this.mediaRecorder.requestData();
        }
      } catch (e) {
        console.warn('requestData before stop:', e);
      }
      this.mediaRecorder.stop();
      await Promise.race([
        done,
        new Promise((resolve) => setTimeout(resolve, 120000)),
      ]);
      return;
    }
    if (this.recordedChunks.length > 0) {
      console.warn('⚠️ Recorder already inactive but chunks exist — uploading now');
      this.isRecording = false;
      try {
        await this.finalizeAndUpload();
      } catch (e) {
        console.error('❌ Upload failed:', e);
      }
      return;
    }
    console.warn('⚠️ Recorder not active and no chunks — nothing to upload');
  }

  /**
   * Upload whatever has been recorded so far (tab close, error, visibility hidden).
   */
  async savePartialRecording() {
    if (this._uploadCompleted || this._uploadInFlight || this._partialUploadInFlight) {
      return;
    }
    if (this.uploader && !this.uploader.failed) {
      // Already uploading continuously — just make sure the latest slice is flushed.
      try {
        if (this.mediaRecorder?.state === 'recording') this.mediaRecorder.requestData();
      } catch (e) {
        /* ignore */
      }
      return;
    }
    if (!this.recordedChunks.length) {
      console.warn('⚠️ savePartialRecording: no chunks yet');
      return;
    }
    this._partialUploadInFlight = true;
    try {
      try {
        if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
          this.mediaRecorder.requestData();
        }
      } catch (e) {
        console.warn('requestData in savePartialRecording:', e);
      }
      const blob = new Blob(this.recordedChunks, { type: this.mimeType || 'video/webm' });
      console.log(`📤 Partial upload: ${(blob.size / 1024 / 1024).toFixed(2)} MB (${this.recordedChunks.length} chunks)`);
      const uploadResult = await this.uploadVideo(blob);
      if (uploadResult) {
        console.log('VIDEO SAVE RESPONSE:', uploadResult.status, uploadResult.text);
        this._uploadCompleted = true;
        console.log('✅ Partial upload marked as completed — blocking any further uploads');
      }
    } catch (e) {
      console.error('❌ Partial recording upload failed:', e);
    } finally {
      this._partialUploadInFlight = false;
    }
  }

  /**
   * Finalize and upload video
   */
  async finalizeAndUpload() {
    if (this._uploadCompleted || this._uploadInFlight) {
      return;
    }
    if (this.uploader && !this.uploader.failed) {
      this._uploadInFlight = true;
      try {
        await this.uploader.finish();
        this._uploadCompleted = true;
      } finally {
        this._uploadInFlight = false;
      }
      if (!this.recordedChunks.length) return;
      // Slices handed back after a late failure fall through to the one-shot upload.
      this._uploadCompleted = false;
    }
    try {
      if (this.recordedChunks.length === 0) {
        console.warn('⚠️ No chunks recorded');
        return;
      }
      this._uploadInFlight = true;
      
      const blob = new Blob(this.recordedChunks, { type: this.mimeType });
      const sizeMB = (blob.size / 1024 / 1024).toFixed(2);
      
      console.log(`📦 Final video: ${sizeMB} MB (${this.recordedChunks.length} chunks)`);
      
      // Upload
      await this.uploadVideo(blob);
      this._uploadCompleted = true;

      // Stop mediaRecorder only, but keep AudioContext & Analyser alive for closing TTS
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        try {
          this.mediaRecorder.stop();
        } catch (e) {
          // Ignore
        }
      }
    } catch (error) {
      console.error('❌ Finalization failed:', error);
    } finally {
      this._uploadInFlight = false;
    }
  }

  /**
   * Upload video to backend
   */
  async uploadVideo(blob) {
    if (this._uploadCompleted) {
      console.log('⏭️ Video already uploaded — skipping duplicate upload');
      return { status: 200, text: '{"skipped":true}' };
    }
    console.log('📤 Uploading video with mixed audio...');
    console.log(
      '🎬 Uploading video to backend...',
      'size:',
      blob.size,
      'bytes',
      'sessionId:',
      this.sessionId
    );

    const formData = new FormData();
    formData.append('video', blob, 'interview_with_audio.webm');
    
    try {
      const token = this._candidateToken();
      const apiUrl = getPublicApiOrigin();
      if (!apiUrl) {
        const msg =
          'API origin empty — set REACT_APP_API_URL or REACT_APP_WS_URL in frontend/.env and restart npm start';
        console.error('❌', msg);
        throw new Error(msg);
      }
      const uploadUrl = `${apiUrl}/api/v1/sessions/${this.sessionId}/video-complete`;
      console.log('📤 Video upload:', uploadUrl, 'bytes:', blob.size);

      const response = await fetch(
        uploadUrl,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      console.log('✅ Video upload response status:', response.status);
      const responseText = await response.text();
      console.log('✅ Video upload response body:', responseText);

      if (response.ok) {
        const data = JSON.parse(responseText);
        console.log('✅ Video uploaded successfully:', data.video_path || data.saved_file_path);
        console.log(`📹 Video size: ${(data.video_size / 1024 / 1024).toFixed(2)} MB`);
      } else {
        console.error('❌ Upload failed:', response.status, response.statusText);
        console.error('   Error details:', responseText);
        throw new Error(`Upload failed: ${response.status}`);
      }

      return { status: response.status, text: responseText };
    } catch (error) {
      console.error('❌ Upload error:', error);
      throw error;
    }
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    console.log('🧹 Cleaning up resources...');

    if (this._beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this._beforeUnloadHandler);
      this._beforeUnloadHandler = null;
    }
    
    // Stop media recorder
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop();
      } catch (e) {
        // Ignore
      }
    }
    
    // Disconnect audio sources
    if (this.micSource) {
      try {
        this.micSource.disconnect();
      } catch (e) {
        // Ignore
      }
    }
    
    if (this.questionAudioSource) {
      try {
        this.questionAudioSource.disconnect();
      } catch (e) {
        // Ignore
      }
    }
    
    // Stop microphone tracks
    if (this.micStream) {
      this.micStream.getTracks().forEach(track => track.stop());
    }
    
    // Close audio context
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().then(() => {
        console.log('✅ Audio context closed');
      }).catch(e => {
        console.warn('⚠️ Error closing audio context:', e);
      });
    }
    
    if (this.compositor) {
      this.compositor.stop();
      this.compositor = null;
    }
    // A part still uploading keeps going (its slices are queued / in IndexedDB); the
    // server closes it ~10 min after the last slice if finish() never ran.
    this.uploader = null;

    // Clear chunks
    this.recordedChunks = [];
    this.isInitialized = false;
    this.combinedStream = null;
  }

  /** Hand the current screen share to the compositor (initial share, re-share, or room hand-off). */
  setScreenStream(stream) {
    this.compositor?.setScreenStream(stream);
  }

  /**
   * Get supported MIME type
   */
  getSupportedMimeType() {
    const types = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=vp9',
      'video/webm',
    ];
    
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    
    return 'video/webm';
  }

  getDavidAnalyser() {
    return this.questionAnalyser;
  }

  getMicAnalyser() {
    return this.micAnalyser;
  }

  getAudioContext() {
    return this.audioContext;
  }

  getRecordingStartTime() {
    return this.recordingStartTime;
  }
}

/** Recording slice length (ms) and composited video bitrate — see start() / initialize(). */
VideoRecorderWithMixedAudio.TIMESLICE_MS = 3000;
VideoRecorderWithMixedAudio.COMPOSITE_BPS = 600000;

export default VideoRecorderWithMixedAudio;

